import React, { useState, useEffect } from 'react';
import { isNativeApp } from '@/services/payment-controller';
import { Capacitor } from '@capacitor/core';
import SubscriptionPlans from '@/components/SubscriptionPlans';
import { toast } from "@/hooks/use-toast";

/**
 * Get the specific platform type
 * @returns Platform type (ios, android, web)
 */
export const getPlatformType = (): 'ios' | 'android' | 'web' => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return 'web';
    }
    return Capacitor.getPlatform() as 'ios' | 'android';
  } catch {
    return 'web';
  }
};

/**
 * Hook to safely detect platform on client-side only
 * @returns platform information object
 */
export const usePlatformDetection = () => {
  const [platformInfo, setPlatformInfo] = useState({
    isNative: false,
    isIOS: false,
    isAndroid: false,
    platformType: 'web' as 'ios' | 'android' | 'web',
    isDetected: false
  });

  useEffect(() => {
    // Only run on client-side
    const isNative = isNativeApp();
    const platformType = getPlatformType();
    
    setPlatformInfo({
      isNative,
      isIOS: platformType === 'ios',
      isAndroid: platformType === 'android',
      platformType,
      isDetected: true
    });
  }, []);

  return platformInfo;
};

/**
 * Component that renders the appropriate payment plan UI based on platform
 */
export const PaymentPlanSelector: React.FC = () => {
  const { isNative, platformType, isDetected } = usePlatformDetection();
  
  // Removed toast notification - RevenueCat will handle native payments
  
  // After detection, render the appropriate component
  if (!isDetected) {
    return (
      <div className="flex justify-center items-center p-4">
        <p className="text-sm text-gray-500">Carregando...</p>
      </div>
    );
  }
  
  // Verificação adicional de segurança
  try {
    return <SubscriptionPlans />;
  } catch (error) {
    console.error('Error rendering SubscriptionPlans:', error);
    return (
      <div className="flex justify-center items-center p-4">
        <p className="text-sm text-red-500">Erro ao carregar planos. Tente novamente.</p>
      </div>
    );
  }
};

/**
 * Returns the appropriate subscription plans component based on the platform
 * @returns React component for subscription plans
 */
export const getPaymentComponent = (): React.ReactNode => {
  // Use our component approach for safer client-side rendering
  return <PaymentPlanSelector />;
};
